import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import SEO from "@/components/SEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AdminEditor() {
  const { id } = useParams();
  const editMode = !!id;
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "",
    meta_title: "", meta_description: "", published: true,
    // English localisation
    title_en: "", excerpt_en: "", content_en: "",
    meta_title_en: "", meta_description_en: "",
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [tab, setTab] = useState("sq"); // "sq" or "en"

  useEffect(() => {
    if (!editMode) return;
    api.get("/blog/posts", { params: { published_only: false, limit: 200 } })
      .then((r) => {
        const found = (r.data || []).find((x) => x.id === id);
        if (found) {
          setForm({
            title: found.title || "",
            slug: found.slug || "",
            excerpt: found.excerpt || "",
            content: found.content || "",
            cover_image: found.cover_image || "",
            tags: (found.tags || []).join(", "),
            meta_title: found.meta_title || "",
            meta_description: found.meta_description || "",
            published: !!found.published,
            title_en: found.title_en || "",
            excerpt_en: found.excerpt_en || "",
            content_en: found.content_en || "",
            meta_title_en: found.meta_title_en || "",
            meta_description_en: found.meta_description_en || "",
          });
        }
      });
  }, [id, editMode]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editMode) await api.put(`/blog/posts/${id}`, payload);
      else await api.post(`/blog/posts`, payload);
      toast.success(editMode ? "Postimi u përditësua" : "Postimi u krijua");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ruajtja dështoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title={editMode ? "Edito Postimin" : "Postim i ri"} noindex />
      <section className="border-b border-line bg-white">
        <div className="container-akm flex items-center justify-between py-5">
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate2 hover:text-ink"><ArrowLeft size={14} /> Kthehu te Admin</Link>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPreview((v) => !v)} className="btn-ghost text-xs" data-testid="editor-preview-toggle">
              {preview ? "Edito" : "Pamje paraprake"}
            </button>
          </div>
        </div>
      </section>

      <form onSubmit={submit} className="bg-bg">
        <div className="container-akm grid gap-6 py-8 md:grid-cols-12">
          <div className="md:col-span-8">
            <div className="card-akm">
              {/* Language tabs */}
              <div className="mb-4 inline-flex rounded-md border border-line p-0.5 text-xs font-semibold">
                {[
                  { k: "sq", label: "🇦🇱 Shqip" },
                  { k: "en", label: "🇬🇧 English" },
                ].map((tt) => (
                  <button
                    key={tt.k}
                    type="button"
                    onClick={() => setTab(tt.k)}
                    className={`rounded-md px-3 py-1.5 transition-colors ${
                      tab === tt.k ? "bg-[#0F172A] text-white" : "text-slate2 hover:text-ink"
                    }`}
                    data-testid={`editor-lang-${tt.k}`}
                  >
                    {tt.label}
                  </button>
                ))}
              </div>

              <input
                type="text"
                placeholder={tab === "sq" ? "Titulli i postimit" : "English title (leave empty to reuse Albanian)"}
                value={tab === "sq" ? form.title : form.title_en}
                onChange={set(tab === "sq" ? "title" : "title_en")}
                required={tab === "sq"}
                className="w-full border-0 bg-transparent p-0 font-display text-2xl font-extrabold tracking-tight text-ink outline-none placeholder:text-slate2/60"
                data-testid={`editor-title-${tab}`}
              />
              {tab === "sq" && (
                <div className="mt-2 flex gap-2 text-xs">
                  <span className="text-slate2">Slug:</span>
                  <input
                    type="text"
                    placeholder="auto-gjenerohet"
                    value={form.slug}
                    onChange={set("slug")}
                    className="flex-1 border-0 bg-transparent p-0 text-xs text-[#FF7A00] outline-none"
                    data-testid="editor-slug"
                  />
                </div>
              )}
              {tab === "en" && (
                <p className="mt-2 text-[11px] text-slate3">
                  The English version shares the same slug (<code className="rounded bg-bg px-1 py-0.5 text-[10px] text-[#FF7A00]">/en/blog/{form.slug || "…"}</code>). Leave any English field empty to fall back to the Albanian original.
                </p>
              )}
            </div>

            <div className="mt-4 card-akm">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate2">
                {tab === "sq" ? "Përmbledhja" : "Excerpt (EN)"}
              </label>
              <textarea
                value={tab === "sq" ? form.excerpt : form.excerpt_en}
                onChange={set(tab === "sq" ? "excerpt" : "excerpt_en")}
                rows={2}
                placeholder={tab === "sq" ? "Përmbledhje e shkurtër për preview…" : "Short English preview…"}
                className="mt-2 w-full rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-[#FF7A00]"
                data-testid={`editor-excerpt-${tab}`}
              />
            </div>

            <div className="mt-4 card-akm">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate2">
                {tab === "sq" ? "Përmbajtja (Markdown)" : "Content (Markdown, EN)"}
              </label>
              {preview ? (
                <div className="prose prose-slate mt-3 max-w-none" data-testid="editor-preview">
                  <ReactMarkdown>{tab === "sq" ? form.content : form.content_en}</ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={tab === "sq" ? form.content : form.content_en}
                  onChange={set(tab === "sq" ? "content" : "content_en")}
                  required={tab === "sq"}
                  rows={20}
                  placeholder={tab === "sq" ? "# Titulli\n\nShkruani përmbajtjen në markdown..." : "# Heading\n\nWrite the English version in markdown..."}
                  className="mt-2 w-full rounded-md border border-line bg-white px-3 py-2 font-mono text-sm leading-relaxed outline-none focus:border-[#FF7A00]"
                  data-testid={`editor-content-${tab}`}
                />
              )}
            </div>
          </div>

          <aside className="md:col-span-4 space-y-4">
            <div className="card-akm">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate2">Statusi</label>
              <label className="mt-2 flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="editor-published" />
                I publikuar (publik)
              </label>
              <button type="submit" disabled={loading} className="btn-primary mt-4 w-full justify-center disabled:opacity-60" data-testid="editor-submit">
                {loading ? "Duke ruajtur…" : (editMode ? "Përditëso" : "Publiko")}
              </button>
            </div>

            <div className="card-akm">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate2">Imazhi i kopertinës (URL)</label>
              <input type="url" value={form.cover_image} onChange={set("cover_image")} placeholder="https://…"
                className="mt-2 w-full rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-[#FF7A00]"
                data-testid="editor-cover" />
              {form.cover_image && (
                <img src={form.cover_image} alt="" className="mt-3 w-full rounded-md border border-line" />
              )}
            </div>

            <div className="card-akm">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate2">Tags (të ndara me presje)</label>
              <input type="text" value={form.tags} onChange={set("tags")} placeholder="pms, channel-manager"
                className="mt-2 w-full rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-[#FF7A00]"
                data-testid="editor-tags" />
            </div>

            <div className="card-akm">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate2">SEO ({tab.toUpperCase()})</label>
              <input
                type="text"
                value={tab === "sq" ? form.meta_title : form.meta_title_en}
                onChange={set(tab === "sq" ? "meta_title" : "meta_title_en")}
                placeholder={tab === "sq" ? "Meta title" : "Meta title (EN)"}
                className="mt-2 w-full rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-[#FF7A00]"
                data-testid={`editor-meta-title-${tab}`}
              />
              <textarea
                value={tab === "sq" ? form.meta_description : form.meta_description_en}
                onChange={set(tab === "sq" ? "meta_description" : "meta_description_en")}
                rows={3}
                placeholder={tab === "sq" ? "Meta description" : "Meta description (EN)"}
                className="mt-2 w-full rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-[#FF7A00]"
                data-testid={`editor-meta-desc-${tab}`}
              />
            </div>
          </aside>
        </div>
      </form>
    </>
  );
}
